import { AxiosProgressEvent } from 'axios';
import { ApiResult, FileDetails } from 'common/models';
import { checkIfCategoryHasSubCategoryOrIndicator } from 'common/utils/table';
import { HttpClient } from 'common/services/http-client.service';

import {
    ConfigurationWhatsNewVM,
    ConfigurationDetails,
    ConfigurationIndicatorVM,
    ConfigurationListRequest,
    ConfigurationReorderUpdateList,
    ConfigurationSaveRequest,
    DeleteConfigRequestType,
    DeleteConfigurationRequest,
    TrendingDataSaveRequest,
    ConfigurationWhatsNewDeleteRequest,
    WhatsNewIndicatorSaveRequest,
    ConfigurationSaveLinkRequest,
    IndicatorSourceDTO,
    VideoLinkDTO,
    VideoTypesArray,
    VideoDetailsPayload,
    DeleteVideoLinkPayload
} from 'modules/configuration/models/configuration.model';
import { getWhatsNewList } from 'assets/data/dataList';

import configurationApiConfig from './configuration.config';
import { IErrorApiResponse } from 'common/models/api-service/error-response.model';
import { ConfigurationTypes, WhatsNewLinkType } from '../enums/configuration.enum';
import { PayloadAction } from '@reduxjs/toolkit';
import { PagedRequest } from 'common/models/data/pagedRequest.model';
import { PaginatedApiResponse } from 'common/models/api-service/api-response.model';
import { Entity, EntityVM } from 'common/models/data/entity.model';

export class ConfigurationService extends HttpClient {
    public static getTrendingData() {
        return ConfigurationService.invokeApi<ConfigurationIndicatorVM[] | []>(
            configurationApiConfig.getTrendingData()
        );
    }

    public static deleteTrendingData(payload: DeleteConfigRequestType) {
        return ConfigurationService.invokeApi<ConfigurationIndicatorVM[] | []>(
            configurationApiConfig.deleteTrendingData(payload)
        );
    }

    public static getKeyIndicators(
        payload: ConfigurationListRequest
    ): Promise<ApiResult<ConfigurationDetails | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationData(payload)
        );
    }

    public static async getStaticIndicators(
        payload: ConfigurationListRequest
    ): Promise<ApiResult<ConfigurationDetails | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationData(payload)
        );
    }

    public static async fetchWhatsNew(): Promise<
        ApiResult<ConfigurationWhatsNewVM[] | IErrorApiResponse> | undefined
    > {
        return ConfigurationService.invokeApi<ConfigurationWhatsNewVM[] | IErrorApiResponse>(
            configurationApiConfig.getWhatsNewData()
        );
    }

    public static async getFrequentlyUsed(
        payload: ConfigurationListRequest
    ): Promise<ApiResult<ConfigurationDetails | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationData(payload)
        );
    }

    public static async getVideoLink(): Promise<ApiResult<any | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationVideoLink()
        );
    }
    public static async getVideoTypes(): Promise<ApiResult<VideoTypesArray> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationVideoTypes()
        );
    }

    public static getGeographyOverview(
        payload: ConfigurationListRequest
    ): Promise<ApiResult<ConfigurationDetails | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<ConfigurationDetails | IErrorApiResponse>(
            configurationApiConfig.getConfigurationData(payload)
        );
    }

    public static async deleteData(
        payload: DeleteConfigurationRequest
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.deleteConfigurationData(payload)
        );
    }

    public static async deleteWhatsNewData(
        payload: ConfigurationWhatsNewDeleteRequest
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.deleteWhatsNewConfigurationData(payload)
        );
    }

    public static async reorderData(
        payload: ConfigurationReorderUpdateList[]
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.reorderConfigurationData(payload)
        );
    }

    public static async reorderTrendingData(
        payload: ConfigurationReorderUpdateList[]
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.reorderTrendingData(payload)
        );
    }

    public static async reorderWhatsNewConfiguration(
        payload: ConfigurationReorderUpdateList[]
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.reorderWhatsNewConfiguration(payload)
        );
    }

    public static async getCategories(
        pagedRequest: PagedRequest
    ): Promise<ApiResult<PaginatedApiResponse<Entity[]> | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<PaginatedApiResponse<Entity[]> | IErrorApiResponse>(
            configurationApiConfig.getCategoriesUrl(pagedRequest)
        );
    }

    public static async getEntitiesByCategoryId(
        categoryId: string
    ): Promise<ApiResult<EntityVM[] | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<EntityVM[] | IErrorApiResponse>(
            configurationApiConfig.getEntitiesByCategoryIdUrl(categoryId)
        );
    }

    public static async getEntitiesByHierarchyId(
        hierarchyId: string
    ): Promise<ApiResult<EntityVM[] | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<EntityVM[] | IErrorApiResponse>(
            configurationApiConfig.getEntitiesByHierarchyIdUrl(hierarchyId)
        );
    }

    public static saveIndicatorAdd(
        payload: ConfigurationSaveRequest<ConfigurationTypes>
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.saveConfiguration(payload)
        );
    }

    public static saveVideoLinkAdd(
        payload: VideoDetailsPayload
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.saveVideoLink(payload)
        );
    }
    public static updateVideoLinkAdd(
        payload: VideoDetailsPayload
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.updateVideoLink(payload)
        );
    }


    public static saveWhatsNewIndicator(
        payload: WhatsNewIndicatorSaveRequest<WhatsNewLinkType>
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.saveWhatsNewIndicator(payload)
        );
    }

    public static saveTrendingData(
        payload: TrendingDataSaveRequest
    ): Promise<ApiResult<boolean | IErrorApiResponse> | undefined> {
        return ConfigurationService.invokeApi<boolean | IErrorApiResponse>(
            configurationApiConfig.saveTrendingConfiguration(payload)
        );
    }

    /**
     * Following method is to invoke get upload file log api
     * @returns
     */
    public static async getUploadFileLog(payload: any): Promise<ApiResult<unknown> | undefined> {
        return this.invokeApi(configurationApiConfig.getUploadFileLog(payload));
    }

    public static async uploadFile(
        whatsNewTypes: WhatsNewLinkType,
        file: FileDetails[],
        onProgress: (event: AxiosProgressEvent) => void,
        id: string | null,
        name: string,
        description?: string,
        existingFileReplaced?: boolean
    ): Promise<ApiResult<boolean> | undefined> {
        const header = {
            'content-type': 'multipart/form-data'
        };
        const additionalConfig: { onUploadProgress?: (event: AxiosProgressEvent) => void } = {};
        const formData = new FormData();
        if (existingFileReplaced) {
            additionalConfig['onUploadProgress'] = onProgress;
            formData.append('file', file[0]);
            formData.append('fileType', file[0].type);
        }
        formData.append('whatsNewTypes', `${whatsNewTypes}`);

        if (id) {
            formData.append('id', id);
        }

        formData.append('name', name);
        if (description) {
            formData.append('description', description);
        }
        return await this.invokeApi(
            configurationApiConfig.uploadFile(formData, header, additionalConfig)
        );
    }

    public static async saveWhatsNewLink({
        payload
    }: PayloadAction<ConfigurationSaveLinkRequest>): Promise<ApiResult<boolean> | undefined> {
        return await this.invokeApi(configurationApiConfig.saveWhatsNewLink(payload));
    }

    public static async getConfigBlobSasUrl(
        blobName: string,
        whatsNewTypes: WhatsNewLinkType
    ): Promise<ApiResult<string> | undefined> {
        const payload = { fileName: blobName, whatsNewTypes };
        return await this.invokeApi(configurationApiConfig.getConfigBlobSasUrl(payload));
    }

    public static async getIndicatorSources(payload: PagedRequest) {
        return await this.invokeApi(configurationApiConfig.fetchIndicatorSources(payload));
    }

    public static async addIndicatorSource(payload: IndicatorSourceDTO) {
        return await this.invokeApi(configurationApiConfig.addIndicatorSource(payload));
    }

    public static async updateIndicatorSource(payload: IndicatorSourceDTO) {
        return await this.invokeApi(configurationApiConfig.updateIndicatorSource(payload));
    }

    public static async deleteIndicatorSource(payload: IndicatorSourceDTO) {
        return await this.invokeApi(configurationApiConfig.deleteIndicatorSource(payload));
    }

    public static async deleteVideoLink(payload: DeleteVideoLinkPayload) {
        return await this.invokeApi(configurationApiConfig.deleteVideoLink(payload));
    }
}
